import { CircleCheck } from "lucide-react";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Numeral } from "../../../../components/numeral/Numeral";

export const ChargesTooltip = ({
  icon,
  items = [],
  textInfo,
  title = "RESUMEN DE CARGOS",
  isInfo = false,
}) => {
  const [open, setOpen] = useState(false);
  const [, setIsMobile] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);

  const normalizedItems = useMemo(() => {
    if (isInfo) return items;
    let updated = [...items];
    const firmaIndex = updated.findIndex(
      (item) => item.label === "Firma electrónica",
    );
    const dctoIndex = updated.findIndex(
      (item) => item.label === "DCTO firma electrónica",
    );
    const firma = firmaIndex !== -1 ? updated[firmaIndex] : null;
    const dcto = dctoIndex !== -1 ? updated[dctoIndex] : null;
    if (dcto && !firma) {
      updated.splice(dctoIndex, 1);
      return updated;
    }
    if (firma && dcto && firma.value === dcto.value) {
      updated[firmaIndex] = { ...firma, value: 0 };
      updated.splice(dctoIndex, 1);
    }
    return updated;
  }, [items, isInfo]);

  const total = useMemo(() => {
    if (isInfo) return 0;
    return normalizedItems.reduce((acc, item) => {
      if (
        item.label === "DCTO firma electrónica" ||
        item.label === "DCTO Abono Firma" ||
        item.label === "DCTO recompensa"
      ) {
        return acc - item.value;
      }
      return acc + item.value;
    }, 0);
  }, [normalizedItems, isInfo]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ✅ Calcula posición real del tooltip al abrir
  const calcPosition = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const tooltipWidth = 320;
    let left = rect.right - tooltipWidth; // alineado a la derecha del trigger

    // Evita que se salga por la izquierda en móvil
    if (left < 8) left = 8;
    // Evita que se salga por la derecha
    if (left + tooltipWidth > window.innerWidth - 8) {
      left = window.innerWidth - tooltipWidth - 8;
    }

    setTooltipPos({
      top: rect.top + window.scrollY - 8, // aparece encima del trigger
      left: left + 25,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleOpen = () => {
    calcPosition();
    setOpen(true);
  };

  return (
    <div
      className="relative"
      ref={wrapperRef}
      onClick={(e) => {
        e.stopPropagation();
        open ? setOpen(false) : handleOpen();
      }}
      onMouseEnter={handleOpen}
      onMouseLeave={() => setOpen(false)}
    >
      {icon}

      {/* ✅ Portal: renderiza en document.body, fuera de cualquier overflow */}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            onMouseEnter={handleOpen}
            onMouseLeave={() => setOpen(false)}
            style={{
              position: "absolute",
              top: tooltipPos.top,
              left: tooltipPos.left,
              zIndex: 9999,
              transform: "translateY(-100%)", // sube el tooltip encima del trigger
            }}
            className="w-80 max-w-[90vw] rounded-2xl bg-[#241F4E] p-5 text-white shadow-xl"
          >
            {/* Flecha apuntando hacia abajo */}
            <div className="absolute -bottom-2 right-6 h-4 w-4 rotate-45 bg-[#241F4E]" />

            {textInfo && (
              <p className="text-xs font-normal text-white/70 mb-4">
                {textInfo}
              </p>
            )}
            <p className="text-xs font-normal text-white/70 mb-4">{title}</p>

            <div className="space-y-2 text-sm">
              {isInfo
                ? normalizedItems.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <CircleCheck className="size-4 mt-0.5" />
                      <span>{item.label}</span>
                    </div>
                  ))
                : normalizedItems.map((item, index) =>
                    item.value > 0 ? (
                      <div key={index} className="flex justify-between">
                        <span>{item.label}</span>
                        {item.label === "DCTO firma electrónica" ||
                        item.label === "DCTO recompensa" ||
                        item.label === "DCTO Abono Firma" ? (
                          <span>
                            -<Numeral format={"$0,0"} value={item.value} />
                          </span>
                        ) : (
                          <Numeral format={"$0,0"} value={item.value} />
                        )}
                      </div>
                    ) : null,
                  )}
            </div>

            {!isInfo && (
              <>
                <div className="my-4 border-t border-white/20" />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  {total > 0 ? <Numeral format={"$0,0"} value={total} /> : "$0"}
                </div>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};
